'use client';
import React from 'react';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import Button from '@src/v3/components/coreUI/button';
import Icon from '@src/v3/components/coreUI/icon';
import GoogleLoginButton from '@src/v3/components/ssoLogin/loginWithGoogle';
import { LoginStepProps } from '@src/v3/interfaces/components/loginModal';

interface AppleErrorStepProps extends LoginStepProps {
  errorType: 'private_relay' | 'email_not_found' | 'general';
  onPhoneLogin?: () => void;
}

const AppleErrorStep: React.FC<AppleErrorStepProps> = ({
  onGoogleOAuth,
  onPhoneLogin,
  errorType = 'private_relay',
}) => {
  const getErrorContent = () => {
    switch (errorType) {
      case 'private_relay':
        return {
          title: 'Private Relay is not supported on Zeme.',
          subtitle: 'Choose from the additional options listed below',
          appleButtonText: 'Apple ID Disabled',
          whyText: 'Why?',
        };
      case 'email_not_found':
        return {
          title: 'Email not found in your Apple account.',
          subtitle: 'Choose from the additional options listed below',
          appleButtonText: 'Apple ID Disabled',
          whyText: 'Why?',
        };
      default:
        return {
          title: 'Apple login is currently unavailable.',
          subtitle: 'Choose from the additional options listed below',
          appleButtonText: 'Apple ID Disabled',
          whyText: 'Why?',
        };
    }
  };

  const errorContent = getErrorContent();

  const handleGoogleSuccess = async (token: string) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${token}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch user info from Google');
      }
      const userInfo = await response.json();
      if (!userInfo.email) {
        throw new Error('No email received from Google');
      }
      await onGoogleOAuth?.(token, {
        name: userInfo.name || '',
        email: userInfo.email,
        picture: userInfo.picture || '',
      });
    } catch (error) {
      console.error('Failed to fetch Google user info:', error);
    }
  };

  const handlePhoneLogin = () => {
    onPhoneLogin?.();
  };

  return (
    <div className="h-full flex flex-col">
      <DialogHeader className="flex flex-col items-center justify-center pt-20 xs:pt-10">
        <div className="flex items-center justify-center mb-4">
          <div className="w-11 h-11 bg-red-700 rounded-full flex items-center justify-center">
            <Icon
              iconName="crossicon"
              iconProps={{ className: 'w-6 h-6 text-white' }}
            />
          </div>
        </div>
        <h2 className="text-2xl font-semibold text-gray-1000 text-center mb-2">
          {errorContent.title}
        </h2>
        <p className="text-lg text-gray-1000 text-center">
          {errorContent.subtitle}
        </p>
      </DialogHeader>

      <div className="flex-1 flex flex-col justify-end space-y-3 mb-16 xs:mb-0">
        {/* Apple ID Disabled Button */}
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            size="xxl"
            className="w-full shadow-none bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
            disabled={true}
          >
            <Icon iconName="apple" iconProps={{ className: 'w-5 h-5' }} />
            {errorContent.appleButtonText}
          </Button>
        </div>

        {/* Google Login Button */}
        <GoogleLoginButton
          onSuccess={handleGoogleSuccess}
          isLoading={false}
          disabled={false}
        />

        {/* Phone Number Login Button */}
        <Button
          type="button"
          variant="outline"
          size="xxl"
          className="w-full shadow-none"
          onClick={handlePhoneLogin}
        >
          <Icon iconName="call" iconProps={{ className: 'w-4 h-4' }} />
          Continue with Phone Number
        </Button>
      </div>
    </div>
  );
};

export default AppleErrorStep;
